import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { Box, Text, Button, StyledTag, Anchor } from '@zillow/constellation';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { getReadTimeWithDefault } from '../../utils/getReadTimeWithDefault.js';
import withPreload from '../../utils/with-preload.js';
import Source from '../Source/Source.js';
import { getPostCardImage } from './image-util.js';

var _excluded = ["url", "image", "title", "excerpt", "size", "category", "readTime", "alignment", "loading", "forceHideReadTime", "forceShowCategory"];
var Tag = /*#__PURE__*/styled(StyledTag).withConfig({
  componentId: "sc-1ln4qre-0"
})(["text-decoration:none;"]);
var StyledAnchor = /*#__PURE__*/styled(Anchor).withConfig({
  componentId: "sc-1ln4qre-1"
})(["text-decoration:none;color:inherit !important;"]);
var SourcePreload = withPreload(Source);
var PreloadBox = withPreload(Box);
var styles = {
  vertical: {
    sm: {
      headingFontType: 'bodySmall',
      borderRadius: '$radii.xl',
      containerStyles: {
        columnGap: 0,
        gridTemplateRows: 'auto 1fr',
        height: '100%'
      },
      bodyStyles: {
        gridColumnEnd: 'span 12',
        padding: '$spacing.xs 0 0 0',
        justifyContent: 'start'
      },
      imageStyles: {
        gridColumnEnd: 'span 12'
      }
    },
    md: {
      containerStyles: {
        columnGap: 0,
        gridTemplateRows: 'auto 1fr',
        height: '100%'
      },
      headingFontType: 'body',
      borderRadius: '$radii.xl',
      bodyStyles: {
        gridColumnEnd: 'span 12',
        padding: '$spacing.sm',
        justifyContent: 'start'
      },
      imageStyles: {
        gridColumnEnd: 'span 12'
      }
    },
    lg: {
      containerStyles: {
        columnGap: 0,
        gridTemplateRows: 'auto 1fr',
        height: '100%'
      },
      headingFontType: 'h4',
      excerptFontType: 'bodySmall',
      excerptColor: 'textMedium',
      borderRadius: '$radii.xxl',
      bodyStyles: {
        gridColumnEnd: 'span 12',
        padding: '$spacing.md',
        justifyContent: 'start'
      },
      imageStyles: {
        gridColumnEnd: 'span 12'
      }
    },
    xl: {
      headingFontType: {
        default: 'h3',
        lg_lte: 'bodyHeading'
      },
      excerptFontType: 'body',
      borderRadius: '$radii.xxl',
      excerptColor: 'textMedium',
      containerStyles: {
        columnGap: 0,
        backgroundColor: 'transparent',
        boxShadow: 'none',
        gridTemplateRows: 'auto 1fr',
        height: '100%'
      },
      imageStyles: {
        gridColumnEnd: 'span 12'
      },
      bodyStyles: {
        gridColumnEnd: 'span 12',
        padding: '$spacing.md $spacing.xs',
        justifyContent: 'start'
      }
    }
  },
  horizontal: {
    sm: {},
    md: {
      headingFontType: 'body',
      borderRadius: '$radii.xl',
      containerStyles: {
        '@media $lg_lte': {
          columnGap: 0,
          gridTemplateRows: 'auto 1fr',
          height: '100%'
        }
      },
      bodyStyles: {
        gridColumnEnd: 'span 6',
        padding: '$spacing.md $spacing.md $spacing.md 0',
        '@media $lg_lte': {
          padding: '$spacing.md',
          gridColumnEnd: 'span 12'
        }
      },
      imageStyles: {
        gridColumnEnd: 'span 6',
        '@media $lg_lte': {
          gridColumnEnd: 'span 12'
        }
      }
    },
    lg: {
      headingFontType: {
        default: 'h3',
        lg_lte: 'h4'
      },
      excerptFontType: 'body',
      excerptColor: 'textMedium',
      borderRadius: '$radii.xxl',
      containerStyles: {
        columnGap: '$spacing.xxl',
        '@media $md_lte': {
          columnGap: 0,
          gridTemplateRows: 'auto 1fr',
          height: '100%'
        }
      },
      bodyStyles: {
        gridColumnEnd: 'span 6',
        padding: '$spacing.xxl $spacing.xxl $spacing.xxl 0',
        '@media $md_lte': {
          padding: '$spacing.md',
          gridColumnEnd: 'span 12'
        }
      },
      imageStyles: {
        gridColumnEnd: 'span 6',
        '@media $md_lte': {
          gridColumnEnd: 'span 12'
        }
      }
    },
    xl: {
      headingFontType: {
        default: 'h3',
        lg_lte: 'h4'
      },
      excerptFontType: 'body',
      borderRadius: '$radii.xxl',
      excerptColor: 'textMedium',
      containerStyles: {
        backgroundColor: 'transparent',
        boxShadow: 'none',
        '@media $md_lte': {
          columnGap: 0
        }
      },
      imageStyles: {
        gridColumnEnd: 'span 7',
        '@media $md_lte': {
          gridColumnEnd: 'span 12'
        }
      },
      bodyStyles: {
        gridColumn: 'span 5',
        padding: '0 0 0 calc( $spacing.xxl - $spacing.md )',
        '@media $md_lte': {
          padding: '$spacing.md $spacing.xs',
          gridColumn: 'span 12'
        }
      }
    }
  }
};
var PostCard = function PostCard(_ref) {
  var _styles$alignment, _styles$alignment2, _styles$alignment3, _styles$alignment4, _styles$alignment5, _styles$alignment6, _styles$alignment7;
  var url = _ref.url,
    image = _ref.image,
    title = _ref.title,
    excerpt = _ref.excerpt,
    size = _ref.size,
    category = _ref.category,
    readTime = _ref.readTime,
    alignment = _ref.alignment,
    loading = _ref.loading,
    forceHideReadTime = _ref.forceHideReadTime,
    forceShowCategory = _ref.forceShowCategory,
    rest = _objectWithoutProperties(_ref, _excluded);
  var headingFontType = styles === null || styles === void 0 || (_styles$alignment = styles[alignment]) === null || _styles$alignment === void 0 || (_styles$alignment = _styles$alignment[size]) === null || _styles$alignment === void 0 ? void 0 : _styles$alignment.headingFontType;
  var excerptFontType = styles === null || styles === void 0 || (_styles$alignment2 = styles[alignment]) === null || _styles$alignment2 === void 0 || (_styles$alignment2 = _styles$alignment2[size]) === null || _styles$alignment2 === void 0 ? void 0 : _styles$alignment2.excerptFontType;
  var excerptColor = styles === null || styles === void 0 || (_styles$alignment3 = styles[alignment]) === null || _styles$alignment3 === void 0 || (_styles$alignment3 = _styles$alignment3[size]) === null || _styles$alignment3 === void 0 ? void 0 : _styles$alignment3.excerptColor;
  var borderRadius = (styles === null || styles === void 0 || (_styles$alignment4 = styles[alignment]) === null || _styles$alignment4 === void 0 || (_styles$alignment4 = _styles$alignment4[size]) === null || _styles$alignment4 === void 0 ? void 0 : _styles$alignment4.borderRadius) || '$radii.xl';
  var bodyStyles = (styles === null || styles === void 0 || (_styles$alignment5 = styles[alignment]) === null || _styles$alignment5 === void 0 || (_styles$alignment5 = _styles$alignment5[size]) === null || _styles$alignment5 === void 0 ? void 0 : _styles$alignment5.bodyStyles) || {};
  var imageStyles = (styles === null || styles === void 0 || (_styles$alignment6 = styles[alignment]) === null || _styles$alignment6 === void 0 || (_styles$alignment6 = _styles$alignment6[size]) === null || _styles$alignment6 === void 0 ? void 0 : _styles$alignment6.imageStyles) || {};
  var containerStyles = (styles === null || styles === void 0 || (_styles$alignment7 = styles[alignment]) === null || _styles$alignment7 === void 0 || (_styles$alignment7 = _styles$alignment7[size]) === null || _styles$alignment7 === void 0 ? void 0 : _styles$alignment7.containerStyles) || {};
  var hasBoxShadow = !(size === 'sm' && alignment === 'vertical');
  var cardImage = getPostCardImage({
    size: size,
    alignment: alignment,
    image: image
  });
  var badgeLabel = /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'flex',
      marginBottom: '$spacing.xs'
    }
  }, category && (size === 'xl' || forceShowCategory) && /*#__PURE__*/React.createElement(Box, {
    sx: {
      zIndex: 1
    }
  }, /*#__PURE__*/React.createElement(Tag, {
    appearance: forceHideReadTime ? 'info' : 'purple',
    size: "sm",
    textTransform: "none",
    marginRight: "xs",
    as: "a",
    href: category.link
  }, /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: category.name
    }
  }))), !forceHideReadTime && /*#__PURE__*/React.createElement(Tag, {
    appearance: "info",
    size: "sm",
    textTransform: "none",
    marginRight: "xs",
    as: "span"
  }, getReadTimeWithDefault(readTime), " min read"));
  return /*#__PURE__*/React.createElement(Box, _extends({}, rest, {
    sx: Object.assign({
      display: 'grid',
      position: 'relative',
      gridTemplateColumns: 'repeat(12, 1fr)',
      borderRadius: "" + borderRadius,
      boxShadow: hasBoxShadow ? '$shadows.shadow300' : 'none',
      columnGap: '$spacing.md',
      textDecoration: 'none',
      backgroundColor: '$colors.white'
    }, containerStyles)
  }), url && size !== 'xl' && /*#__PURE__*/React.createElement(Box, _extends({
    as: "a",
    sx: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    }
  }, url)), (cardImage === null || cardImage === void 0 ? void 0 : cardImage.sourceUrl) && /*#__PURE__*/React.createElement(Box, {
    sx: Object.assign({}, imageStyles),
    as: "picture"
  }, (cardImage === null || cardImage === void 0 ? void 0 : cardImage.sizes) && Object.keys(cardImage === null || cardImage === void 0 ? void 0 : cardImage.sizes).map(function (breakpoint) {
    var _cardImage$sizes, _cardImage$sizes2, _cardImage$sizes3;
    return /*#__PURE__*/React.createElement(SourcePreload, {
      key: breakpoint,
      media: breakpoint,
      srcSet: cardImage === null || cardImage === void 0 || (_cardImage$sizes = cardImage.sizes) === null || _cardImage$sizes === void 0 || (_cardImage$sizes = _cardImage$sizes[breakpoint]) === null || _cardImage$sizes === void 0 ? void 0 : _cardImage$sizes.sourceUrl,
      width: cardImage === null || cardImage === void 0 || (_cardImage$sizes2 = cardImage.sizes) === null || _cardImage$sizes2 === void 0 || (_cardImage$sizes2 = _cardImage$sizes2[breakpoint]) === null || _cardImage$sizes2 === void 0 ? void 0 : _cardImage$sizes2.width,
      height: cardImage === null || cardImage === void 0 || (_cardImage$sizes3 = cardImage.sizes) === null || _cardImage$sizes3 === void 0 || (_cardImage$sizes3 = _cardImage$sizes3[breakpoint]) === null || _cardImage$sizes3 === void 0 ? void 0 : _cardImage$sizes3.height,
      loading: loading,
      decoding: loading === 'lazy' ? 'async' : undefined
    });
  }), /*#__PURE__*/React.createElement(PreloadBox, {
    sx: {
      aspectRatio: '16 / 9',
      width: '100%',
      height: '100%',
      borderRadius: "" + borderRadius,
      objectFit: 'cover'
    },
    as: "img",
    src: cardImage.sourceUrl,
    alt: cardImage.alt || title,
    width: cardImage.width,
    height: cardImage.height,
    loading: loading,
    decoding: loading === 'lazy' ? 'async' : undefined
  })), /*#__PURE__*/React.createElement(Box, {
    sx: Object.assign({
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start'
    }, bodyStyles)
  }, badgeLabel, size !== 'xl' ? /*#__PURE__*/React.createElement(Text, {
    fontType: headingFontType,
    as: "h2",
    dangerouslySetInnerHTML: {
      __html: title
    }
  }) : /*#__PURE__*/React.createElement(Text, {
    fontType: headingFontType,
    as: "h2"
  }, /*#__PURE__*/React.createElement(StyledAnchor, _extends({}, url, {
    dangerouslySetInnerHTML: {
      __html: title
    }
  }))), excerpt && /*#__PURE__*/React.createElement(Text, {
    fontType: excerptFontType,
    as: "p",
    marginTop: "sm",
    fontColor: excerptColor,
    dangerouslySetInnerHTML: {
      __html: excerpt
    }
  }), size === 'xl' && url && /*#__PURE__*/React.createElement(Button, _extends({
    size: "md",
    as: "a",
    buttonType: "primary",
    marginTop: "md"
  }, url), "Read article")));
};
var SizePropType = process.env.NODE_ENV !== "production" ? PropTypes.shape({
  sourceUrl: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired
}) : {};
PostCard.propTypes = process.env.NODE_ENV !== "production" ? {
  alignment: PropTypes.oneOf(['vertical', 'horizontal']),
  image: PropTypes.shape({
    sourceUrl: PropTypes.string.isRequired,
    altText: PropTypes.string.isRequired,
    mediaDetails: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
      sizes: PropTypes.shape({
        small: SizePropType,
        medium: SizePropType,
        large: SizePropType,
        full: SizePropType
      })
    })
  }),
  category: PropTypes.shape({
    name: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired
  }),
  readTime: PropTypes.number,
  url: PropTypes.shape({
    href: PropTypes.string.isRequired
  }),
  title: PropTypes.string.isRequired,
  excerpt: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  children: PropTypes.node,
  loading: PropTypes.string,
  forceShowCategory: PropTypes.bool,
  forceHideReadTime: PropTypes.bool
} : {};
PostCard.defaultProps = {
  alignment: 'vertical',
  loading: 'lazy',
  size: 'lg',
  excerpt: null,
  readTime: 5,
  image: {
    sourceUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAlgAAAHCBAMAAADBXapXAAAAJ1BMVEX29vrR0dXa2t7T09f09Pju7vLW1trk5Ojx8fXh4eXp6e3l5enc3OB7H3z4AAAE2ElEQVR42uzasUocURTH4VMo7sbqMLBBYrOVabVLKklnt5Bgv0UgpEreIKTYOkLsA75AhDxgVld3Vta594ql3/cOl/n/DhMAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAwDONfweNRtPJj6DNeebXoMn+cWZ+C1pc5NLhaVC3yFtfgqrded7q/gQ113nndVBxlmtvg6LRPNeMrYqr3PAmKNjLnrFVtjPNJQ+xfWJt+hwMeHXTOR5ia+dsOQge9TMfUD2Vztk2mQUDndNTPcXOGeDEXOwcY6viYw56Hzywnz1jq2znIgsOPcSBzjG26p1T1P0N7n3PNWOr4kMueYhP7hxjq+IqBzgxbxlnk19BjKa55CE2Oc8BTsy1zlE95c5pdvDSf6xZZFl3dGxs3dmtdM7RbPN2M3nZP9ZcZ8nJv5tuVD0rZ1nQXZ6uwtGJuT8lD3g3i5WxsVXpnO6y/2DOnZgj9nLQyafoLYyt/+zcsW/TQBTH8TfYagnTc9REbRgwFapEJw+IRuqQCaTAECoBqrpQCSS6EVr2wJABlqCKDoghQQywVQgkxFbxj+E0je+efXexYWC432fu9JV7vty9RI7MSJsd+crEEfNDtmgckTTx/mTrcuJY2aUl74+Y37HZ9Y7hbz0/Yn7t/heUbnt+xLzPJtu9EhuyBvnG9hY0u8868o15I2rzCrEcy5VUSxBLiHquS1jE0t3qkNU9RizdjY7rcAKxdO/dc26IpdSPyKHGiKVE38mlj1jiNehyiRGL51bdrYIYsUhsGRyGjFhUstXyPmIROVoFD/QHC7GI7K3Ck7F2/I5YKWurO8lTfT+KWClLq+CTfpFaY8SaMrcKD8UV/QSxzhmnrboxc9M46Ob3sXI0MC1X8sq5zwbbv/ybaetRwQ6nWu7r/YMvBETBIU+NXQ/WwWeCVHicn5JZQiqLMOapes/6YLV/EqilPXXTduYXvSSY2U248GBNWKl/8O8NaLPDF1bMD9YGfghD+4QzNzZ9KmxjXZdbhpk1yiwn6k4fsiqqFZ9SZoj/wKLwjDPNwgFphO2C3F4pz/PTa5t4B9paNUk5m10nglKLWTOSn3Q28FiJVtbhxz7XsWO3tpJjtSG38RK0t5JfYRpiZS+s7cIWKT8IhJglv7+kugB+maeCv/8ZgjcfyTcsRFRSsPfbx3tD4VnJVLuxn5esuvqgZCpfb6R1a1TC3rq/1/cVl/fusc+zDportEj4wu/BkArLe/AVX3TKDMjpbez9yJHSIpfwBPNZrIyc24UEw2xU7vdluuuY/BOxVsgm+IYxyXMlNll3Y0YsGavheKwQKx+rY3usEGuOM1eNwyIJI5Yh1qp5ZhKxFFZOKedRwohlibVSXNkRS7Ae/YUxI1YOa57IUVzEcsWKxHgpYhWwbqRGJhFrUaxWtlwhlgkLvYsv0CFWiVhbs6UdscrEagymrRDLgqXHaSvEKhmrnjBiWXEeYiEWYi2AWBUgVgWIVQFiVYBYFSBWBYhVAWJVgFgVINb/dO0fEAAAAAAAAAD8YQ8OBAAAAACA/F8bQVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVaQ8OSAAAAAAE/X/dj1ABAAAAAAAAAAAAAACAlQB0Y5H7ngzE2AAAAABJRU5ErkJggg==',
    altText: '',
    mediaDetails: {
      sizes: {
        full: {
          width: 720,
          height: 405,
          sourceUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAlgAAAHCBAMAAADBXapXAAAAJ1BMVEX29vrR0dXa2t7T09f09Pju7vLW1trk5Ojx8fXh4eXp6e3l5enc3OB7H3z4AAAE2ElEQVR42uzasUocURTH4VMo7sbqMLBBYrOVabVLKklnt5Bgv0UgpEreIKTYOkLsA75AhDxgVld3Vta594ql3/cOl/n/DhMAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAwDONfweNRtPJj6DNeebXoMn+cWZ+C1pc5NLhaVC3yFtfgqrded7q/gQ113nndVBxlmtvg6LRPNeMrYqr3PAmKNjLnrFVtjPNJQ+xfWJt+hwMeHXTOR5ia+dsOQge9TMfUD2Vztk2mQUDndNTPcXOGeDEXOwcY6viYw56Hzywnz1jq2znIgsOPcSBzjG26p1T1P0N7n3PNWOr4kMueYhP7hxjq+IqBzgxbxlnk19BjKa55CE2Oc8BTsy1zlE95c5pdvDSf6xZZFl3dGxs3dmtdM7RbPN2M3nZP9ZcZ8nJv5tuVD0rZ1nQXZ6uwtGJuT8lD3g3i5WxsVXpnO6y/2DOnZgj9nLQyafoLYyt/+zcsW/TQBTH8TfYagnTc9REbRgwFapEJw+IRuqQCaTAECoBqrpQCSS6EVr2wJABlqCKDoghQQywVQgkxFbxj+E0je+efXexYWC432fu9JV7vty9RI7MSJsd+crEEfNDtmgckTTx/mTrcuJY2aUl74+Y37HZ9Y7hbz0/Yn7t/heUbnt+xLzPJtu9EhuyBvnG9hY0u8868o15I2rzCrEcy5VUSxBLiHquS1jE0t3qkNU9RizdjY7rcAKxdO/dc26IpdSPyKHGiKVE38mlj1jiNehyiRGL51bdrYIYsUhsGRyGjFhUstXyPmIROVoFD/QHC7GI7K3Ck7F2/I5YKWurO8lTfT+KWClLq+CTfpFaY8SaMrcKD8UV/QSxzhmnrboxc9M46Ob3sXI0MC1X8sq5zwbbv/ybaetRwQ6nWu7r/YMvBETBIU+NXQ/WwWeCVHicn5JZQiqLMOapes/6YLV/EqilPXXTduYXvSSY2U248GBNWKl/8O8NaLPDF1bMD9YGfghD+4QzNzZ9KmxjXZdbhpk1yiwn6k4fsiqqFZ9SZoj/wKLwjDPNwgFphO2C3F4pz/PTa5t4B9paNUk5m10nglKLWTOSn3Q28FiJVtbhxz7XsWO3tpJjtSG38RK0t5JfYRpiZS+s7cIWKT8IhJglv7+kugB+maeCv/8ZgjcfyTcsRFRSsPfbx3tD4VnJVLuxn5esuvqgZCpfb6R1a1TC3rq/1/cVl/fusc+zDportEj4wu/BkArLe/AVX3TKDMjpbez9yJHSIpfwBPNZrIyc24UEw2xU7vdluuuY/BOxVsgm+IYxyXMlNll3Y0YsGavheKwQKx+rY3usEGuOM1eNwyIJI5Yh1qp5ZhKxFFZOKedRwohlibVSXNkRS7Ae/YUxI1YOa57IUVzEcsWKxHgpYhWwbqRGJhFrUaxWtlwhlgkLvYsv0CFWiVhbs6UdscrEagymrRDLgqXHaSvEKhmrnjBiWXEeYiEWYi2AWBUgVgWIVQFiVYBYFSBWBYhVAWJVgFgVINb/dO0fEAAAAAAAAAD8YQ8OBAAAAACA/F8bQVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVaQ8OSAAAAAAE/X/dj1ABAAAAAAAAAAAAAACAlQB0Y5H7ngzE2AAAAABJRU5ErkJggg=='
        }
      }
    }
  },
  url: null
};
/** @component */
var PostCard$1 = PostCard;

export { PostCard$1 as default };
